import React, { useCallback, useMemo, useState } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { HorizontalFilterBarItem } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { HorizontalFilterBarItemProps } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { UseFiltersHook } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE } from 'libs/end-to-end/src/progress';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { GROUP_BY_DEFAULT_ITEM_KEY } from 'libs/end-to-end/src/filters-v2/filter-utils';

export const EventStudyMenu = ({
  singleDateItem,
  eventStudyGroupItem,
  closeMenu,
  filterPayload,
}: {
  singleDateItem: HorizontalFilterBarItemProps;
  eventStudyGroupItem: HorizontalFilterBarItemProps;
  closeMenu: () => void;
  filterPayload: UseFiltersHook;
}) => {
  const language = useLanguage();
  const [singleDateMenuOpen, setSingleDateMenuOpen] = useState(false);
  const [eventStudyGroupMenuOpen, setEventStudyGroupMenuOpen] = useState(false);

  const canApply = useMemo(() => {
    const singleDate = filterPayload.primaryFilterSelections.find(
      (s) => s.primaryFilterKey === 'singleDate',
    );
    if (!singleDate?.value) {
      return false;
    }

    return singleDate.value !== EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE;
  }, [filterPayload.primaryFilterSelections]);

  const onApply = useCallback(() => {
    filterPayload.manualApply();
    closeMenu();
  }, [closeMenu, filterPayload]);

  const handleClearTreatmentGroup = useCallback(
    () =>
      filterPayload.setFilters({
        primaryFilterSelections: [
          {
            primaryFilterKey: 'eventStudyGroup',
            value: [],
          },
          {
            primaryFilterKey: 'groupBy',
            value: GROUP_BY_DEFAULT_ITEM_KEY,
          },
        ],
        secondaryFilterSelections: [],
        shouldPreventAutoSubmit: true,
      }),
    [filterPayload],
  );

  const handleClearTreatmentDate = useCallback(
    () =>
      filterPayload.setFilters({
        primaryFilterSelections: [
          {
            primaryFilterKey: 'singleDate',
            value: EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE,
          },
        ],
        secondaryFilterSelections: [],
        shouldPreventAutoSubmit: true,
      }),
    [filterPayload],
  );

  //w-[372px] h-[469px]
  return (
    <div className={'py-3 px-6 bg-white rounded-lg shadow-4px w-[372px]'}>
      <Spacing direction="vertical" size="gap-1">
        <S2Typography
          as={'span'}
          translatedContent={language.eventStudyMenuHeading()}
          variant={'headline-6-alt'}
          textColorVariant={'gray-900'}
        />

        <S2Typography
          as={'span'}
          translatedContent={language.eventStudyMenuDescription()}
          variant={'body-7'}
          textColorVariant={'gray-900'}
        />

        <div className="pt-3">
          <S2Typography
            as={'span'}
            translatedContent={language.eventStudyMenuDateLabel()}
            variant={'body-7'}
            textColorVariant={'gray-900'}
          />
        </div>

        <Spacing direction="horizontal" size="sm" className="mt-2 items-center">
          <HorizontalFilterBarItem
            {...singleDateItem}
            key={singleDateItem.key}
            menuOpened={singleDateMenuOpen}
            openMenu={() => setSingleDateMenuOpen(true)}
            closeMenu={() => setSingleDateMenuOpen(false)}
          />
          <TouchTarget
            disabled={false}
            behavior={{
              type: 'onClick',
              onClick: handleClearTreatmentDate,
            }}
            content={() => {
              return (
                <S2Typography
                  as={'span'}
                  translatedContent={language.reset()}
                  variant={'body-7'}
                  textColorVariant={'gray-500'}
                />
              );
            }}
          />
        </Spacing>

        <S2Typography
          as={'span'}
          translatedContent={language.eventStudyMenuDateDescription()}
          variant={'body-7'}
          textColorVariant={'gray-500'}
          dangerouslyOverrideClassNameFn={(cn) => `${cn} mt-2`}
        />

        <hr className="border-0 border-t border-[#E7E8E8] my-4" />

        <S2Typography
          as={'span'}
          translatedContent={language.eventStudyMenuGroupLabel()}
          variant={'body-7'}
          textColorVariant={'gray-900'}
        />

        <Spacing direction="horizontal" size="sm" className="mt-2 items-center">
          <HorizontalFilterBarItem
            {...eventStudyGroupItem}
            key={eventStudyGroupItem.key}
            menuOpened={eventStudyGroupMenuOpen}
            openMenu={() => setEventStudyGroupMenuOpen(true)}
            closeMenu={() => setEventStudyGroupMenuOpen(false)}
          />
          <TouchTarget
            disabled={false}
            behavior={{
              type: 'onClick',
              onClick: handleClearTreatmentGroup,
            }}
            content={() => {
              return (
                <S2Typography
                  as={'span'}
                  translatedContent={language.reset()}
                  variant={'body-7'}
                  textColorVariant={'gray-500'}
                />
              );
            }}
          />
        </Spacing>

        <S2Typography
          as={'span'}
          translatedContent={language.eventStudyMenuGroupDescription()}
          variant={'body-7'}
          textColorVariant={'gray-500'}
          dangerouslyOverrideClassNameFn={(cn) => `${cn} mt-2`}
        />

        <PrimaryButtonLightBg
          onClick={onApply}
          disabled={!canApply}
          translatedContent={'Apply'}
          className="mt-4"
        />
      </Spacing>
    </div>
  );
};
